import { AfterViewInit, Component, HostListener, Inject, LOCALE_ID, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import * as L from 'leaflet';

import { GncProgramsService } from '../../../api/gnc-programs.service';
import { AuthService } from '../../../auth/auth.service';
import { ObservationsService } from '../observations.service';
import { MainConfig } from '../../../../conf/main.config';
import { BaseDetailComponent, markerIcon } from '../../base/detail/detail.component';
import { getNaturalHistoryFieldLabel, getNaturalHistoryLabel, NaturalHistoryField } from '../natural-history';
import { TaxonomyListItem, ObservationFeature } from '../observation.model';

const map_conf = {
    BASE_LAYERS: MainConfig['BASEMAPS'].reduce((acc, baseLayer: Object) => {
        const layerConf: any = {
            name: baseLayer['name'], attribution: baseLayer['attribution'],
            detectRetina: baseLayer['detectRetina'], maxZoom: baseLayer['maxZoom'],
            bounds: baseLayer['bounds'], apiKey: baseLayer['apiKey'],
            layerName: baseLayer['layerName'],
        };
        if (baseLayer['subdomains']) { layerConf.subdomains = baseLayer['subdomains']; }
        acc[baseLayer['name']] = L.tileLayer(baseLayer['layer'], layerConf);
        return acc;
    }, {}),
    DEFAULT_BASE_MAP: () => map_conf.BASE_LAYERS[MainConfig['DEFAULT_PROVIDER']],
};

interface PublicComment {
    id_comment: number;
    parent_id: number | null;
    root_id: number | null;
    body: string;
    created_at: string;
    author: { username: string; avatar: string; id_role: number };
    is_author: boolean;
    score: number;
    viewer_vote: number;
    depth: number;
}

@Component({
    selector: 'app-obs-detail',
    templateUrl: './detail.component.html',
    styleUrls: ['./detail.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class ObsDetailComponent extends BaseDetailComponent implements AfterViewInit, OnDestroy {
    currentLanguage = 'pt-BR';
    loading = true;
    error = false;
    galleryStartIndex = 0;
    mapExpanded = false;
    taxonomyItem: TaxonomyListItem = null;
    taxonOptions: TaxonomyListItem[] = [];
    speciesQuery = '';
    selectedTaxon: TaxonomyListItem = null;
    showTaxonSuggestions = false;
    viewerAvatar = '';
    viewerUsername = '';

    social: any = null;
    socialError = false;
    socialLoading = false;
    related: ObservationFeature[] = [];
    relatedSameSpecies = false;
    showQualityCriteriaInfo = false;
    commentText = '';
    commentSort = 'relevant';
    commentPage = 1;
    replyTo: number = null;
    sendingComment = false;
    saving = false;
    submittingIdentification = false;
    suggestionCdNom: number = null;
    actionMessage = '';
    actionError = false;
    openMenu = '';
    collapsedThreads: number[] = [];
    private detailMap: L.Map = null;
    mobileLayout = window.innerWidth <= 820;
    mobileSections = { facts: false, quality: false, identification: false };

    @HostListener('window:resize')
    onWindowResize(): void {
        const previous = this.mobileLayout;
        this.mobileLayout = window.innerWidth <= 820;
        if (previous && !this.mobileLayout && this.obs && !this.detailMap) {
            setTimeout(() => this.setupMap(), 50);
        }
        if (this.detailMap) { setTimeout(() => this.detailMap && this.detailMap.invalidateSize(), 100); }
    }

    toggleSection(section: 'facts' | 'quality' | 'identification'): void {
        if (!this.mobileLayout) { return; }
        this.mobileSections[section] = !this.mobileSections[section];
        if (section === 'facts' && this.mobileSections.facts) {
            setTimeout(() => {
                if (!this.detailMap) { this.setupMap(); }
                else { this.detailMap.invalidateSize(); }
            }, 80);
        }
    }

    isSectionOpen(section: 'facts' | 'quality' | 'identification'): boolean {
        return !this.mobileLayout || !!this.mobileSections[section];
    }

    constructor(
        @Inject(LOCALE_ID) public localeId: string,
        private http: HttpClient,
        private route: ActivatedRoute,
        private router: Router,
        private programService: GncProgramsService,
        public auth: AuthService,
        private observationsService: ObservationsService
    ) {
        super();
        this.currentLanguage = localStorage.getItem('language') === 'fr' ? 'fr' : 'pt-BR';
        this.route.params.subscribe((params) => {
            const observationId = Number(params['obs_id']);
            const programId = Number(params['program_id']);
            this.obs_id = Number.isFinite(observationId) && observationId > 0 ? observationId : null;
            this.program_id = Number.isFinite(programId) && programId > 0 ? programId : null;
        });
        this.module = 'observations';
    }

    t(pt: string, fr: string): string { return this.currentLanguage === 'fr' ? fr : pt; }
    get isAuthenticated(): boolean { return this.auth.authorized$.getValue(); }

    get matchingSpecies(): TaxonomyListItem[] {
        const query = (this.speciesQuery || '').trim().toLocaleLowerCase();
        if (query.length < 2) { return []; }
        return this.taxonOptions.filter((item) => {
            const taxon = item.taxref;
            return [taxon.nom_vern, taxon.lb_nom].some(
                (value) => (value || '').toLocaleLowerCase().includes(query)
            );
        }).slice(0, 8);
    }

    chooseSpecies(species: TaxonomyListItem): void {
        if (!species || !species.taxref) { return; }
        this.selectedTaxon = species;
        this.suggestionCdNom = Number(species.taxref.cd_nom);
        this.speciesQuery = species.taxref.nom_vern || species.taxref.lb_nom;
        this.showTaxonSuggestions = false;
    }

    ngAfterViewInit(): void {
        if (!this.obs_id || !this.program_id) {
            this.loading = false; this.error = true; return;
        }
        this.loadObservation();
        this.loadViewer();
    }

    private loadViewer(): void {
        this.viewerUsername = '';
        this.viewerAvatar = '';
        if (!this.isAuthenticated) { return; }
        this.auth.ensureAuthorized().subscribe(
            (response: any) => {
                const viewer = response && response.features ? response.features : {};
                this.viewerUsername = viewer.username || '';
                this.viewerAvatar = this.resolveAvatar(viewer.avatar);
            },
            () => { this.viewerUsername = ''; this.viewerAvatar = ''; }
        );
    }
    ngOnDestroy(): void {
        document.body.classList.remove('wp-detail-map-open');
        if (this.detailMap) { this.detailMap.remove(); this.detailMap = null; }
    }

    private loadObservation(): void {
        this.loading = true; this.error = false; this.taxonomyItem = null;
        this.programService.getObsDetails(this.obs_id).subscribe(
            (obs: any) => {
                if (!obs || !obs.properties) { this.loading = false; this.error = true; return; }
                this.obs = obs;
                this.photos = (obs.properties.photos || [])
                    .filter((photo) => !!photo && typeof photo.url === 'string')
                    .map((photo) => {
                        const url = this.resolvePhotoUrl(photo.url);
                        return url ? { ...photo, url } : null;
                    }).filter((photo) => !!photo);
                this.galleryStartIndex = 0;
                this.loadNaturalHistoryAttributes();
                this.speciesQuery = '';
                this.suggestionCdNom = null;
                this.selectedTaxon = null;
                this.showTaxonSuggestions = false;
                this.loadTaxonomyItem();
                this.loadSocial();
                this.loadRelated();
                this.loading = false;
                setTimeout(() => this.setupMap(), 0);
            },
            () => { this.loading = false; this.error = true; }
        );
    }

    private loadTaxonomyItem(): void {
        const cdNom = Number(this.obs.properties.cd_nom);
        if (!Number.isFinite(cdNom) || cdNom <= 0) { return; }
        this.http.get<TaxonomyListItem[]>(
            MainConfig.API_ENDPOINT + '/taxonomy/lists/101/species',
            { params: { limit: '300' } }
        ).subscribe(
            (items) => {
                this.taxonOptions = Array.isArray(items) ? items.filter((item) => item && item.taxref) : [];
                this.taxonomyItem = this.taxonOptions.find((item) =>
                    Number(item.taxref.cd_nom) === cdNom) || null;
            },
            () => { this.taxonomyItem = null; this.taxonOptions = []; }
        );
    }

    private loadNaturalHistoryAttributes(): void {
        this.attributes = [];
        const data = this.obs.properties.json_data;
        if (!data) { return; }
        const fields: NaturalHistoryField[] = ['sex', 'age_class', 'behavior'];
        fields.forEach((key) => {
            if (data[key] && data[key] !== 'nao_informado') {
                this.attributes.push({
                    key, name: getNaturalHistoryFieldLabel(key, this.localeId),
                    value: getNaturalHistoryLabel(key, data[key], this.localeId)
                });
            }
        });
        this.loadJsonSchema().subscribe((form: any) => {
            if (!form || !form.json_schema || !form.json_schema.schema || !form.json_schema.layout) { return; }
            const schema = form.json_schema.schema.properties || {};
            for (const item of form.json_schema.layout) {
                if (!item || !item.key || fields.indexOf(item.key) !== -1) { continue; }
                const value = data[item.key];
                if (value !== undefined && value !== null && schema[item.key]) {
                    this.attributes.push({ key: item.key, name: schema[item.key].title, value: String(value) });
                }
            }
        }, () => {});
    }

    private setupMap(): void {
        if (!this.hasCoordinates || !this.isSectionOpen('facts')) { return; }
        const longitude = Number(this.obs.geometry.coordinates[0]);
        const latitude = Number(this.obs.geometry.coordinates[1]);
        if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) { return; }
        if (this.detailMap) { this.detailMap.remove(); this.detailMap = null; }
        const element = document.getElementById('observation-detail-map');
        if (!element) { return; }
        this.detailMap = L.map(element, {
            layers: [map_conf.DEFAULT_BASE_MAP()], zoomControl: true, scrollWheelZoom: false
        } as any);
        const latLng = L.latLng(latitude, longitude);
        this.detailMap.setView(latLng, 11);
        L.marker(latLng, { icon: markerIcon }).addTo(this.detailMap);
    }

    get visiblePhotos(): any[] {
        return this.photos.length <= 3 ? this.photos : this.photos.slice(this.galleryStartIndex, this.galleryStartIndex + 3);
    }
    get showGalleryArrows(): boolean { return this.photos.length >= 4; }
    get galleryClass(): string {
        if (this.photos.length <= 1) { return 'one-photo'; }
        if (this.photos.length === 2) { return 'two-photos'; }
        return 'three-photos';
    }
    get galleryIndicator(): string {
        if (!this.showGalleryArrows) { return ''; }
        return (this.galleryStartIndex + 1) + '–' +
            Math.min(this.galleryStartIndex + 3, this.photos.length) + ' de ' + this.photos.length;
    }
    previousPhotos(): void { this.galleryStartIndex = Math.max(0, this.galleryStartIndex - 1); }
    nextPhotos(): void {
        this.galleryStartIndex = Math.min(Math.max(0, this.photos.length - 3), this.galleryStartIndex + 1);
    }

    get commonName(): string {
        if (this.taxonomyItem && this.taxonomyItem.taxref && this.taxonomyItem.taxref.nom_vern) {
            return this.taxonomyItem.taxref.nom_vern;
        }
        const p = this.obs ? this.obs.properties : null;
        return (p && (p.common_name || p.name)) || this.t('Espécie não identificada', 'Espèce non identifiée');
    }
    get scientificName(): string {
        if (this.taxonomyItem && this.taxonomyItem.taxref && this.taxonomyItem.taxref.lb_nom) {
            return this.taxonomyItem.taxref.lb_nom;
        }
        const p = this.obs ? this.obs.properties : null;
        return p ? p.sci_name || (p.taxref && p.taxref.lb_nom) || '' : '';
    }
    get observerName(): string {
        const p = this.obs ? this.obs.properties : null;
        return (p && ((p.observer && p.observer.username) || p.obs_txt)) || this.t('Anônimo', 'Anonyme');
    }
    get observerHandle(): string {
        const p = this.obs && this.obs.properties;
        return p && p.observer && p.observer.username
            ? p.observer.username : this.observerName;
    }
    get observerAvatar(): string {
        const p = this.obs ? this.obs.properties : null;
        return this.resolveAvatar(p && p.observer && p.observer.avatar);
    }
    get locationLabel(): string {
        const value = this.obs && this.obs.properties ? this.obs.properties.municipality : null;
        return value && value !== 'None' && value !== 'None (None)' ? value : this.t('Local não informado', 'Lieu non renseigné');
    }
    get observationComment(): string {
        const value = this.obs && this.obs.properties ? this.obs.properties.comment : '';
        return value && String(value).trim() ? String(value).trim() :
            this.t('Nenhuma descrição foi informada para este registro.', 'Aucune description pour cette observation.');
    }
    get sexLabel(): string { return this.naturalHistoryValue('sex'); }
    get ageClassLabel(): string { return this.naturalHistoryValue('age_class'); }
    get behaviorLabel(): string { return this.naturalHistoryValue('behavior'); }
    get habitatLabel(): string {
        const habitat = this.attributes.find((item) => item && item.name && /habitat|hábitat/i.test(String(item.name)));
        return habitat ? habitat.value : this.t('Não informado', 'Non renseigné');
    }
    get photoAuthor(): string { return this.photos.length && this.photos[0].author ? this.photos[0].author : this.observerName; }
    get photoLicense(): string {
        const p = this.photos.length ? this.photos[0] : null;
        const license = p && p.license;
        return license === 'ARR' ? this.t('Todos os direitos reservados', 'Tous droits réservés')
            : license || this.t('Não informada', 'Non renseignée');
    }
    get dataLicense(): string {
        const p = this.obs && this.obs.properties;
        return p && p.data_license === 'ARR' ?
            this.t('Todos os direitos reservados', 'Tous droits réservés')
            : p && p.data_license ? String(p.data_license) : this.t('Não informada', 'Non renseignée');
    }
    // Uma licença só ganha link quando há uma URL HTTPS cadastrada ou
    // quando seu identificador Creative Commons contém uma versão explícita.
    private resolveLicenseUrl(license: string, rawUrl: any): string {
        const supplied = typeof rawUrl === 'string' ? rawUrl.trim() : '';
        if (/^https:\/\/[^\s/?#]+(?:[/?#][^\s]*)?$/i.test(supplied)) {
            return supplied;
        }
        const code = String(license || '').trim().replace(/\s+/g, ' ').toUpperCase();
        if (/^CC0(?: 1\.0)?$/.test(code)) {
            return 'https://creativecommons.org/publicdomain/zero/1.0/';
        }
        const cc = /^CC (BY(?:-NC)?(?:-SA|-ND)?) (4\.0|3\.0|2\.0)$/.exec(code);
        return cc ? 'https://creativecommons.org/licenses/' +
            cc[1].toLowerCase() + '/' + cc[2] + '/' : '';
    }
    get photoLicenseUrl(): string {
        const photo = this.photos.length ? this.photos[0] : null;
        return this.resolveLicenseUrl(this.photoLicense, photo && photo.license_url);
    }
    get dataLicenseUrl(): string {
        const data = this.obs && this.obs.properties;
        return this.resolveLicenseUrl(this.dataLicense, data && data.data_license_url);
    }
    get licenseDetailsUrl(): string {
        return this.photoLicenseUrl || this.dataLicenseUrl;
    }
    get hasCoordinates(): boolean {
        const coordinates = this.obs && this.obs.geometry && this.obs.geometry.coordinates;
        return !!(Array.isArray(coordinates) && coordinates.length === 2 &&
            Number.isFinite(Number(coordinates[0])) && Number.isFinite(Number(coordinates[1])));
    }
    openSpecies(): void {
        const cd = this.obs && this.obs.properties ? Number(this.obs.properties.cd_nom) : NaN;
        if (Number.isFinite(cd) && cd > 0) { this.router.navigate(['/synthesis/species', cd]); }
    }
    scrollTo(id: string): void {
        if (['record-info', 'record-map', 'record-comments', 'related-records'].indexOf(id) === -1) { return; }
        const el = document.getElementById(id);
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    }
    toggleMapExpanded(): void {
        if (!this.detailMap) { return; }
        this.mapExpanded = !this.mapExpanded;
        document.body.classList.toggle('wp-detail-map-open', this.mapExpanded);
        setTimeout(() => { if (this.detailMap) { this.detailMap.invalidateSize(); } }, 120);
    }
    loadJsonSchema() { return this.http.get(this.URL + '/programs/' + this.program_id + '/customform/'); }
    private naturalHistoryValue(key: string): string {
        const found = this.attributes.find((item) => item && item.key === key);
        return found ? found.value : this.t('Não informado', 'Non renseigné');
    }
    private resolvePhotoUrl(rawValue: string): string {
        const url = String(rawValue || '').trim();
        if (!url) { return null; }
        if (/^\/media\/[a-zA-Z0-9_.-]+$/.test(url)) { return MainConfig.API_ENDPOINT + url; }
        if (/^https?:\/\//i.test(url)) {
            try {
                const parsed = new URL(url);
                const permitted = [new URL(MainConfig.API_ENDPOINT).origin, window.location.origin];
                return permitted.indexOf(parsed.origin) !== -1 ? url : null;
            } catch (_) { return null; }
        }
        return null;
    }
    resolveAvatar(raw: string): string {
        const value = String(raw || '').replace(/^\/media\//, '');
        return /^[a-zA-Z0-9_.-]{1,200}$/.test(value) ? MainConfig.API_ENDPOINT + '/media/' + value : '';
    }
    avatarInitial(name: string): string { return (name || '?').slice(0, 1).toUpperCase(); }

    loadRelated(): void {
        this.related = [];
        this.relatedSameSpecies = false;
        this.observationsService.getRecentObservations(80).subscribe(
            (result) => {
                const candidates = (result.features || []).filter((item) =>
                    item && item.properties &&
                    Number(item.properties.id_observation) !== Number(this.obs_id)
                );
                const sameSpecies = candidates.filter((item) =>
                    Number(item.properties.cd_nom) === Number(this.obs.properties.cd_nom)
                );
                const others = candidates.filter((item) =>
                    Number(item.properties.cd_nom) !== Number(this.obs.properties.cd_nom)
                );
                // A prioridade é a espécie atual; os demais são explicitamente
                // rotulados como outros registros da comunidade, não da mesma espécie.
                this.relatedSameSpecies = sameSpecies.length > 0;
                this.related = sameSpecies.concat(others).slice(0, 4);
            }, () => { this.related = []; }
        );
    }
    relatedPhoto(record: ObservationFeature): string {
        const photos = record && record.properties ? record.properties.photos : [];
        return photos && photos.length ? this.resolvePhotoUrl(photos[0].url) : '';
    }
    openAllRelated(): void {
        if (this.relatedSameSpecies) { this.openSpecies(); }
        else { this.router.navigate(['/programs', this.program_id, 'observations']); }
    }
    openRelated(record: ObservationFeature): void {
        if (record && record.properties && record.properties.id_observation) {
            this.router.navigate(['/programs', record.properties.id_program || this.program_id,
                'observations', record.properties.id_observation]);
        }
    }

    loadSocial(): void {
        this.socialLoading = true; this.socialError = false;
        this.http.get<any>(MainConfig.API_ENDPOINT + '/observations/' + this.obs_id + '/social', {
            params: { page: String(this.commentPage), per_page: '10', sort: this.commentSort }
        }).subscribe(
            (response) => { this.social = response; this.socialLoading = false; },
            () => { this.socialError = true; this.socialLoading = false; }
        );
    }
    sortComments(sort: string): void {
        if (['relevant', 'recent', 'oldest'].indexOf(sort) === -1) { return; }
        this.commentSort = sort; this.commentPage = 1; this.collapsedThreads = []; this.loadSocial();
    }
    changePage(delta: number): void {
        if (!this.social) { return; }
        const page = this.commentPage + delta;
        if (page < 1 || page > this.social.pages) { return; }
        this.commentPage = page; this.loadSocial();
    }
    showReply(comment: PublicComment): void {
        this.replyTo = this.replyTo === comment.id_comment ? null : comment.id_comment;
        this.commentText = '';
        this.scrollTo('record-comments');
    }
    isThreadCollapsed(comment: PublicComment): boolean {
        if (!comment.parent_id) { return false; }
        return this.collapsedThreads.indexOf(comment.root_id || comment.parent_id) !== -1;
    }
    hasReplies(id: number): boolean {
        return !!(this.social && this.social.comments &&
            this.social.comments.some((comment) => comment.root_id === id));
    }
    toggleThread(id: number): void {
        const index = this.collapsedThreads.indexOf(id);
        if (index === -1) { this.collapsedThreads.push(id); }
        else { this.collapsedThreads.splice(index, 1); }
    }
    submitComment(): void {
        if (!this.isAuthenticated) { this.notify(this.t('Entre na sua conta para comentar.', 'Connectez-vous pour commenter.'), true); return; }
        const body = this.commentText.trim();
        if (!body || body.length > 1500 || this.sendingComment) { return; }
        this.sendingComment = true;
        this.http.post(MainConfig.API_ENDPOINT + '/observations/' + this.obs_id + '/comments', {
            body, parent_id: this.replyTo
        }).subscribe(
            () => {
                this.commentText = ''; this.replyTo = null; this.sendingComment = false;
                this.commentSort = 'recent'; this.commentPage = 1; this.loadSocial();
                this.notify(this.t('Comentário publicado.', 'Commentaire publié.'));
            },
            () => { this.sendingComment = false; this.notify(this.t('Não foi possível comentar.', 'Impossible de commenter.'), true); }
        );
    }
    vote(comment: PublicComment, value: number): void {
        if (!this.isAuthenticated) { this.notify(this.t('Entre na sua conta para votar.', 'Connectez-vous pour voter.'), true); return; }
        const finalValue = comment.viewer_vote === value ? 0 : value;
        this.http.put<any>(MainConfig.API_ENDPOINT + '/observations/' + this.obs_id +
            '/comments/' + comment.id_comment + '/vote', { value: finalValue }).subscribe(
            (response) => { comment.score = response.score; comment.viewer_vote = response.viewer_vote; },
            () => this.notify(this.t('Falha ao registrar voto.', 'Vote non enregistré.'), true)
        );
    }
    deleteComment(comment: PublicComment): void {
        if (!this.isAuthenticated || !window.confirm(this.t('Excluir este comentário?', 'Supprimer ce commentaire ?'))) { return; }
        this.http.delete(MainConfig.API_ENDPOINT + '/observations/' + this.obs_id +
            '/comments/' + comment.id_comment).subscribe(
            () => { this.loadSocial(); this.notify(this.t('Comentário removido.', 'Commentaire supprimé.')); },
            () => this.notify(this.t('Não foi possível excluir.', 'Impossible de supprimer.'), true)
        );
    }
    toggleSaved(): void {
        if (!this.isAuthenticated) { this.notify(this.t('Entre na sua conta para salvar.', 'Connectez-vous pour enregistrer.'), true); return; }
        if (this.saving || !this.social) { return; }
        this.saving = true;
        const url = MainConfig.API_ENDPOINT + '/observations/' + this.obs_id + '/save';
        const request = this.social.saved ? this.http.delete<any>(url) : this.http.put<any>(url, {});
        request.subscribe(
            (response) => { this.social.saved = response.saved; this.saving = false; },
            () => { this.saving = false; this.notify(this.t('Não foi possível salvar.', 'Impossible d’enregistrer.'), true); }
        );
    }
    share(): void {
        const url = window.location.href;
        const nativeShare = (navigator as any).share;
        if (nativeShare) {
            nativeShare.call(navigator, { title: this.commonName, url }).catch(() => {});
        } else { this.copyText(url, this.t('Link copiado.', 'Lien copié.')); }
    }
    copyCitation(): void {
        const citation = this.photoAuthor + '. ' + this.commonName + '. WikiPRIMATES, observação #' +
            this.obs_id + ' (' + this.obs.properties.date + '). ' + window.location.href +
            '. ' + this.t('Licença da foto:', 'Licence de la photo :') + ' ' + this.photoLicense + '.';
        this.copyText(citation, this.t('Citação copiada.', 'Citation copiée.'));
    }
    private copyText(value: string, success: string): void {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(value).then(() => this.notify(success),
                () => this.notify(this.t('Não foi possível copiar.', 'Copie impossible.'), true));
        } else { this.notify(this.t('Cópia indisponível neste navegador.', 'Copie indisponible.'), true); }
    }
    suggestIdentification(): void {
        if (!this.isAuthenticated || this.submittingIdentification) {
            this.notify(this.t('Entre na sua conta para identificar.', 'Connectez-vous pour identifier.'), true); return;
        }
        const query = this.speciesQuery.trim();
        // Um texto digitado que não corresponde a uma opção selecionada não confirma outra espécie.
        if (query && (!this.selectedTaxon || query !==
            (this.selectedTaxon.taxref.nom_vern || this.selectedTaxon.taxref.lb_nom))) {
            this.notify(this.t('Escolha uma espécie na lista antes de enviar.', 'Sélectionnez une espèce de la liste.'), true);
            return;
        }
        const id = Number(this.selectedTaxon ? this.selectedTaxon.taxref.cd_nom : this.obs.properties.cd_nom);
        if (!Number.isInteger(id) || id < 1) { return; }
        this.submittingIdentification = true;
        this.http.post(MainConfig.API_ENDPOINT + '/observations/' + this.obs_id + '/identifications',
            { cd_nom: id }).subscribe(
            () => { this.submittingIdentification = false; this.loadSocial(); this.notify(this.t('Identificação registrada.', 'Identification enregistrée.')); },
            () => { this.submittingIdentification = false; this.notify(this.t('Não foi possível identificar. Verifique o código da espécie.', 'Identification impossible.'), true); }
        );
    }
    report(reason: string, commentId: number = null): void {
        if (!this.isAuthenticated) { this.notify(this.t('Entre na sua conta para denunciar.', 'Connectez-vous pour signaler.'), true); return; }
        this.openMenu = '';
        this.http.post(MainConfig.API_ENDPOINT + '/observations/' + this.obs_id + '/reports', {
            reason, id_comment: commentId
        }).subscribe(
            () => this.notify(this.t('Denúncia registrada para análise.', 'Signalement enregistré.')),
            () => this.notify(this.t('Não foi possível denunciar.', 'Signalement impossible.'), true)
        );
    }
    toggleMenu(name: string): void { this.openMenu = this.openMenu === name ? '' : name; }
    private notify(message: string, isError: boolean = false): void {
        this.actionMessage = message; this.actionError = isError;
    }
}
